import React, { useState } from "react";
import { Tabs, Tab, Box, Button, Typography, Chip } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import { toast } from "react-toastify";
import { runCode } from "../../../services/service";
import { useCodingLanguages } from "../../../context/CodingLanguageContext";

const ConsoleTabs = ({ question, languageValues }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [testResult, setTestResult] = useState(null);

  const { code, selectedLanguage } = useCodingLanguages();

  const handleRunCode = async () => {
    const examId = sessionStorage.getItem("examId");

    try {
      const data = {
        exam_id: examId,
        subject_id: question.subject_id,
        question_id: question.id,
        code: {
          language: languageValues[selectedLanguage],
          code,
        },
      };

      const response = await runCode(data);
      const result = response;

      setTestResult(result);
      setActiveTab(0);
      toast.success("Your code ran successfully");
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong!");
    }
  };

  // ✅ Count Public Testcases
  const publicPassedCount =
    testResult?.public_results?.filter((t) => t.passed).length || 0;

  const totalPublic = testResult?.public_results?.length || 0;

  // ✅ Count Private Testcases (if available)
  const privatePassedCount =
    testResult?.private_results?.filter((t) => t.passed).length || 0;

  const totalPrivate = testResult?.private_results?.length || 0;

  const isAccepted =
    testResult?.public_passed && testResult?.private_passed;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e8e8e8",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e8e8e8",
          bgcolor: "#fafafa",
          px: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="inherit"
          TabIndicatorProps={{
            style: { backgroundColor: "#ffa116", height: 2 },
          }}
        >
          <Tab
            label="Test Result"
            sx={{
              textTransform: "none",
              minWidth: 90,
              fontSize: "0.8rem",
              color: activeTab === 0 ? "#ff9800" : "#999",
            }}
          />
        </Tabs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TerminalRoundedIcon sx={{ fontSize: 14, color: "#999" }} />
          <Typography variant="caption" sx={{ color: "#999" }}>
            Console
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, bgcolor: "#ffffff" }}>
        {activeTab === 0 && (
          <Box>
            {testResult && (
              <>
                {/* Result Status */}
                <Box mb={2}>
                  <Chip
                    label={isAccepted ? "Accepted" : "Wrong Answer"}
                    size="small"
                    sx={{
                      bgcolor: isAccepted ? "#4caf5022" : "#f4433622",
                      color: isAccepted ? "#388e3c" : "#d32f2f",
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {/* ✅ Testcase Counts */}
                <Box mb={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`Public: ${publicPassedCount}/${totalPublic}`}
                    size="small"
                    sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}
                  />

                  {/* Show private only if exists */}
                  {totalPrivate > 0 ? (
                    <Chip
                      label={`Private: ${privatePassedCount}/${totalPrivate}`}
                      size="small"
                      sx={{ bgcolor: "#fff3e0", color: "#f57c00" }}
                    />
                  ) : (
                    <Chip
                      label={`Private: ${
                        testResult.private_passed ? "Passed" : "Failed"
                      }`}
                      size="small"
                      sx={{ bgcolor: "#fff3e0", color: "#f57c00" }}
                    />
                  )}
                </Box>

                {/* Public Testcases */}
                {testResult.public_results?.map((test, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Input
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        color: "#1976d2",
                      }}
                    >
                      {test.input}
                    </Typography>

                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Expected
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        color: "#388e3c",
                      }}
                    >
                      {test.expected_output}
                    </Typography>

                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Your Output
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        color: test.passed ? "#388e3c" : "#d32f2f",
                      }}
                    >
                      {test.user_output}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {!testResult && (
              <Typography sx={{ color: "#999" }}>
                Click "Run" to see results
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 1.5,
          py: 1,
          borderTop: "1px solid #e8e8e8",
          bgcolor: "#fafafa",
        }}
      >
        <Button
          size="small"
          startIcon={<PlayArrowRoundedIcon />}
          sx={{
            color: "#333",
            textTransform: "none",
            bgcolor: "#f0f0f0",
            "&:hover": { bgcolor: "#e0e0e0" },
          }}
          onClick={handleRunCode}
        >
          Run
        </Button>

        <Button
          size="small"
          startIcon={<UploadRoundedIcon />}
          sx={{
            textTransform: "none",
            bgcolor: "#4caf50",
            color: "#fff",
            fontWeight: 600,
            "&:hover": { bgcolor: "#388e3c" },
          }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default ConsoleTabs;